import { isNil } from 'lodash'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { dateBackendDateTimeToUI, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { Model } from 'modules/core/lib/mobx-rest'

const WorkingHoursFrequency = {
  week: 'week',
  day: 'day',
  month: 'month',
  year: 'year',
}

type BankHolidayTreatment = 'workable' | 'non_workable'
const workable: BankHolidayTreatment = 'workable'

type AnnualWorkingTimeDistribution = 'limit_workdays' | 'limit_daily_hours'

export const buildContractVersion = (attr: Partial<IContractVersion> = {}) => {
  return new ContractVersion(buildContractVersionAttrs(attr))
}

const buildContractVersionAttrs = (attr: Partial<IContractVersion> = {}) => {
  return {
    id: '0',
    employee_id: '0',
    country: null,
    job_title: null,
    job_catalog_role: null,
    job_catalog_level: null,
    job_catalog_level_id: null,
    job_catalog_tree_node_uuid: null,
    starts_on: null,
    ends_on: null,
    effective_on: '1970-01-01',
    has_payroll: false,
    salary_amount: null,
    salary_frequency: null,
    working_week_days: null,
    working_hours: null,
    working_hours_frequency: null,
    max_legal_yearly_hours: null,
    maximum_weekly_hours: null,
    bank_holiday_treatment: workable,
    working_time_percentage_in_cents: null,
    annual_working_time_distribution: null,
    adjusted_daily_minutes: null,
    annual_workdays_count: null,
    min_rest_minutes_between_days: null,
    max_work_minutes_per_day: null,
    es_has_teleworking_contract: null,
    es_cotization_group: null,
    es_contract_observations: null,
    es_job_description: null,
    has_trial_period: null,
    trial_period_ends_on: null,
    es_contract_type_id: null,
    es_working_day_type_id: null,
    es_education_level_id: null,
    es_professional_category_id: null,
    es_contribution_type_id: null,
    es_agreement_code_id: null,
    es_cno_occupation_id: null,
    es_regime_id: null,
    es_tariff_group_id: null,
    es_tariff_group_name: null,
    es_occupation_code_id: null,
    es_classification_id: null,
    es_a3innuva_job_position_id: null,
    fr_employee_type: null,
    fr_forfait_jours: false,
    fr_jours_par_an: null,
    fr_jours_par_an_cents: null,
    fr_coefficient: null,
    fr_contract_type_id: null,
    fr_level_id: null,
    fr_step_id: null,
    fr_mutual_id: null,
    fr_professional_category_id: null,
    fr_work_type_id: null,
    de_contract_type_id: null,
    de_employment_type: null,
    de_flat_rate_tax: null,
    de_activity_type: null,
    de_personal_key_group_id: null,
    pt_contract_type_id: null,
    created_by_name: null,
    created_by_avatar: null,
    created_at: '1970-01-01',
    updated_at: '1970-01-01',
    action_type: null,
    ...attr,
  }
}

interface BasicAttributes {
  id: string // nilable in the backend but it doesn't make sense
  employee_id: string
  country: string | null
  job_title: string | null
  job_catalog_role: string | null
  job_catalog_level: string | null
  job_catalog_level_id: string | null
  job_catalog_tree_node_uuid: string | null
  starts_on: string | null
  ends_on: string | null
  effective_on: string
  has_payroll: boolean
  salary_amount: number | null
  salary_frequency: string | null
  working_week_days: string | null
  working_hours: number | null
  working_hours_frequency: string | null
  max_legal_yearly_hours: number | null
  maximum_weekly_hours: number | null
  bank_holiday_treatment: BankHolidayTreatment
  working_time_percentage_in_cents: number | null
  annual_working_time_distribution: AnnualWorkingTimeDistribution | null
  adjusted_daily_minutes: number | null
  annual_workdays_count: number | null
  min_rest_minutes_between_days: number | null
  max_work_minutes_per_day: number | null
}

interface SpainAttributes {
  es_has_teleworking_contract: boolean | null // nilable from the backend
  es_cotization_group: number | null
  es_contract_observations: string | null
  es_job_description: string | null
  has_trial_period: boolean | null // nilable from the backend
  trial_period_ends_on: string | null
  es_contract_type_id: string | null
  es_working_day_type_id: string | null
  es_education_level_id: string | null
  es_professional_category_id: string | null
  es_contribution_type_id: string | null
  es_agreement_code_id: string | null
  es_cno_occupation_id: string | null
  es_regime_id: string | null
  es_tariff_group_id: string | null
  es_tariff_group_name: string | null
  es_occupation_code_id: string | null
  es_classification_id: string | null
  es_a3innuva_job_position_id: string | null
}

interface FranceAttributes {
  fr_employee_type: string | null
  fr_forfait_jours: boolean
  fr_jours_par_an: number | null
  fr_jours_par_an_cents: number | null
  fr_coefficient: string | null
  fr_contract_type_id: string | null
  fr_level_id: string | null
  fr_step_id: string | null
  fr_mutual_id: string | null
  fr_professional_category_id: string | null
  fr_work_type_id: string | null
}

interface GermanyAttributes {
  de_contract_type_id: string | null
  de_employment_type: number | null
  de_flat_rate_tax: number | null
  de_activity_type: number | null
  de_personal_key_group_id: string | null
}

interface PortugalAttributes {
  pt_contract_type_id: string | null
}

interface MetadataAttributes {
  created_by_name: string | null
  created_by_avatar: string | null
  created_at: string
  updated_at: string
  action_type: string | null
}

export type IContractVersion = BasicAttributes &
  SpainAttributes &
  FranceAttributes &
  GermanyAttributes &
  PortugalAttributes &
  MetadataAttributes

interface FieldValues {
  countryCode?: string
  name?: string
  automationAttribute?: string
  label: string
}

class ContractVersion extends Model<IContractVersion> {
  static SalaryFrequencies: () => { [key: string]: string } = () => ({
    yearly: i18n.t('common.yearly'),
    monthly: i18n.t('common.monthly'),
    weekly: i18n.t('common.weekly'),
    daily: i18n.t('common.daily'),
    hourly: i18n.t('common.hourly'),
  })

  static WorkingHoursFrequency: () => { [key: string]: string } = () => ({
    week: i18n.t('common.week.one'),
    day: i18n.t('common.day.one'),
    month: i18n.t('common.month'),
    year: i18n.t('common.year'),
  })

  static Fields = (): Record<string, FieldValues> => ({
    salary_amount: {
      label: i18n.t('contract_versions.navigator.changes.salary_amount'),
    },
    has_payroll: {
      label: i18n.t('contract_versions.navigator.changes.has_payroll'),
    },
    salary_frequency: {
      label: i18n.t('contract_versions.navigator.changes.salary_frequency'),
    },
    ends_on: {
      label: i18n.t('contract_versions.navigator.changes.ends_on'),
    },
    job_title: {
      label: i18n.t('contract_versions.navigator.changes.job_title'),
    },
    job_catalog_level_id: {
      label: i18n.t('contract_versions.navigator.changes.job_catalog_level_id'),
    },
    job_catalog_tree_node_uuid: {
      label: i18n.t('contract_versions.navigator.changes.job_catalog_tree_node_uuid'),
    },
    starts_on: {
      label: i18n.t('contract_versions.navigator.changes.starts_on'),
    },
    working_week_days: {
      label: i18n.t('contract_versions.navigator.changes.working_week_days'),
    },
    working_hours: {
      label: i18n.t('contract_versions.navigator.changes.working_hours'),
    },
    working_hours_frequency: {
      label: i18n.t('contract_versions.navigator.changes.working_hours_frequency'),
    },
    bank_holiday_treatment: {
      label: i18n.t('contract_versions.navigator.changes.bank_holiday_treatment'),
    },
    working_time_percentage_in_cents: {
      label: i18n.t('contract_versions.navigator.changes.working_time_percentage'),
    },
    annual_working_time_distribution: {
      label: i18n.t('contract_versions.navigator.changes.annual_working_time_distribution'),
    },
    es_contract_observations: {
      countryCode: 'es',
      label: i18n.t('contract_versions.navigator.changes.es_contract_observations'),
    },
    es_contract_type_id: {
      countryCode: 'es',
      automationAttribute: 'contract_type',
      label: i18n.t('contract_versions.navigator.changes.es_contract_type_id'),
    },
    es_cotization_group_id: {
      countryCode: 'es',
      label: i18n.t('contract_versions.navigator.changes.es_cotization_group_id'),
    },
    es_education_level_id: {
      countryCode: 'es',
      automationAttribute: 'education_level',
      label: i18n.t('contract_versions.navigator.changes.es_education_level_id'),
    },
    es_regime_id: {
      countryCode: 'es',
      automationAttribute: 'regime',
      label: i18n.t('contract_versions.navigator.changes.es_regime_id'),
    },
    es_tariff_group_id: {
      countryCode: 'es',
      automationAttribute: 'tariff_group',
      label: i18n.t('contract_versions.navigator.changes.es_tariff_group_id'),
    },
    es_cno_occupation_id: {
      countryCode: 'es',
      automationAttribute: 'cno_occupation',
      label: i18n.t('contract_versions.navigator.changes.es_cno_occupation_id'),
    },
    es_contribution_type_id: {
      countryCode: 'es',
      automationAttribute: 'contribution_type',
      label: i18n.t('contract_versions.navigator.changes.es_contribution_type_id'),
    },
    es_occupation_code_id: {
      countryCode: 'es',
      automationAttribute: 'occupation_code',
      label: i18n.t('contract_versions.navigator.changes.es_occupation_code_id'),
    },
    es_agreement_code_id: {
      countryCode: 'es',
      automationAttribute: 'agreement_code',
      label: i18n.t('contract_versions.navigator.changes.es_agreement_code_id'),
    },
    es_classification_id: {
      countryCode: 'es',
      automationAttribute: 'classification',
      label: i18n.t('contract_versions.navigator.changes.es_classification_id'),
    },
    es_a3innuva_job_position_id: {
      countryCode: 'es',
      automationAttribute: 'a3innuva_job_position',
      label: i18n.t('contract_versions.navigator.changes.es_a3innuva_job_position_id'),
    },
    es_has_teleworking_contract: {
      countryCode: 'es',
      label: i18n.t('contract_versions.navigator.changes.es_has_teleworking_contract'),
    },
    es_job_description: {
      countryCode: 'es',
      label: i18n.t('contract_versions.navigator.changes.es_job_description'),
    },
    es_professional_category_id: {
      countryCode: 'es',
      automationAttribute: 'professional_category',
      label: i18n.t('contract_versions.navigator.changes.es_professional_category_id'),
    },
    trial_period: {
      label: i18n.t('contract_versions.navigator.changes.trial_period'),
    },
    trial_period_ends_on: {
      label: i18n.t('contract_versions.navigator.changes.trial_period_ends_on'),
    },
    es_working_day_type_id: {
      countryCode: 'es',
      automationAttribute: 'working_day_type',
      label: i18n.t('contract_versions.navigator.changes.es_working_day_type_id'),
    },
    fr_coefficient: {
      countryCode: 'fr',
      label: i18n.t('contract_versions.navigator.changes.fr_coefficient'),
    },
    fr_contract_type_id: {
      countryCode: 'fr',
      automationAttribute: 'contract_type',
      label: i18n.t('contract_versions.navigator.changes.fr_contract_type_id'),
    },
    fr_employee_type: {
      countryCode: 'fr',
      label: i18n.t('contract_versions.navigator.changes.fr_employee_type'),
    },
    fr_forfait_jours: {
      countryCode: 'fr',
      label: i18n.t('contract_versions.navigator.changes.fr_forfait_jours'),
    },
    fr_jours_par_an: {
      countryCode: 'fr',
      label: i18n.t('contract_versions.navigator.changes.fr_jours_par_an'),
    },
    fr_level_id: {
      countryCode: 'fr',
      automationAttribute: 'education_level',
      label: i18n.t('contract_versions.navigator.changes.fr_level_id'),
    },
    fr_mutual_id: {
      countryCode: 'fr',
      label: i18n.t('contract_versions.navigator.changes.fr_mutual_id'),
    },
    fr_professional_category_id: {
      countryCode: 'fr',
      automationAttribute: 'professional_category',
      label: i18n.t('contract_versions.navigator.changes.fr_professional_category'),
    },
    fr_step_id: {
      countryCode: 'fr',
      name: 'contracts_fr_step_id',
      label: i18n.t('contract_versions.navigator.changes.fr_step_id'),
    },
    fr_work_type_id: {
      countryCode: 'fr',
      automationAttribute: 'working_day_type',
      label: i18n.t('contract_versions.navigator.changes.fr_work_type_id'),
    },
    job_catalog_role: {
      automationAttribute: 'job_catalog_role',
      label: i18n.t('contract_versions.navigator.changes.job_catalog_role'),
    },
    de_contract_type_id: {
      countryCode: 'de',
      automationAttribute: 'contract_type',
      label: i18n.t('contract_versions.navigator.changes.de_contract_type_id'),
    },
    pt_contract_type_id: {
      countryCode: 'pt',
      automationAttribute: 'contract_type',
      label: i18n.t('contract_versions.navigator.changes.pt_contract_type_id'),
    },
  })

  static CustomContractAttribute = (field: string): string => {
    // when field start with contracts_, remove the contracts_ prefix
    const fieldKey = field.startsWith('contracts_') ? field.substr(10) : field
    const fieldValue = this.Fields()[fieldKey]

    if (fieldValue && 'countryCode' in fieldValue) return field

    return ''
  }

  constructor(attr: IContractVersion) {
    super(attr)
    makeObservable(this, {
      effectiveOn: computed,
      startsOn: computed,
      endsOn: computed,
      terminated: computed,
      hoursPerDayInCents: computed,
      workingDays: computed,
      future: computed,
    })
  }

  static urlRoot() {
    return '/contracts/contract_versions'
  }

  get effectiveOn(): moment.Moment {
    return stringToDate(this.attributes.effective_on, 'backend')
  }

  get startsOn(): moment.Moment | null | undefined {
    // TODO: this function should not longer accept nil values once start_date Phase 2 is deployed
    const startsOn = this.attributes.starts_on

    if (!startsOn) return

    return stringToDate(startsOn, 'backend')
  }

  get endsOn(): moment.Moment | null | undefined {
    const endsOn = this.attributes.ends_on

    if (!endsOn) return

    return stringToDate(endsOn, 'backend')
  }

  get createdAtString(): string | null | undefined {
    const createdAt = this.attributes.created_at

    if (!createdAt) return

    return dateBackendDateTimeToUI(createdAt)
  }

  get authorName(): string {
    return this.attributes.created_by_name || ''
  }

  get authorAvatarUrl(): string {
    return this.attributes.created_by_avatar || ''
  }

  activeWithinDateRange(startDate: moment.Moment, endDate: moment.Moment): boolean {
    return (
      (isNil(this.startsOn) || this.startsOn.isSameOrBefore(endDate, 'day')) &&
      (isNil(this.endsOn) || this.endsOn.isSameOrAfter(startDate, 'day'))
    )
  }

  activeOn(date: moment.Moment): boolean {
    return this.activeWithinDateRange(date, date)
  }

  terminatedOn(date: moment.Moment): boolean {
    return !isNil(this.endsOn) && this.endsOn.isBefore(date)
  }

  get terminated(): boolean {
    return this.terminatedOn(moment())
  }

  get hoursPerDayInCents(): number {
    const days = this.workingDays.length

    switch (this.attributes.working_hours_frequency) {
      case WorkingHoursFrequency.day:
        return this.attributes.working_hours ?? 0

      case WorkingHoursFrequency.week:
        if (days === 0) return 0

        return (this.attributes.working_hours ?? 0) / days

      default:
        return 0
    }
  }

  get workingDays(): string[] {
    const workingDays = this.attributes.working_week_days

    if (!workingDays) return []

    return workingDays.split(',')
  }

  get future(): boolean {
    return this.effectiveOn.isAfter(moment(), 'day')
  }

  get jobCatalogLevelId(): string | null | undefined {
    return this.attributes.job_catalog_level_id
  }

  get jobCatalogTreeNodeUuid(): string | null | undefined {
    return this.attributes.job_catalog_tree_node_uuid
  }

  get isSpanish(): boolean {
    return this.attributes.country === 'es'
  }

  get isFrench(): boolean {
    return this.attributes.country === 'fr'
  }

  get isGerman(): boolean {
    return this.attributes.country === 'de'
  }

  get isPortuguese(): boolean {
    return this.attributes.country === 'pt'
  }

  get hasForfaitJours(): boolean {
    return this.attributes.fr_forfait_jours ?? false
  }
}

export default ContractVersion
